import React, { useCallback, useState, useRef, useEffect } from "react";
import { isEmpty, isEqual } from "lodash";
import moment from "moment";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { IconButton } from "@mui/material";

import Field from "components/Field";
import Select from "components/Input/Select";
import Input from "components/Input";
import InputRow from "components/Input/Row";
import CostTableInput from "components/Input/CostTableTotal";
import CostTableHeader from "components/CostTableHeader";
import Accordion from "components/Accordion";
import InputWithUnits from "components/Input/InputWithUnits";
import CostPerAreaInput from "components/Input/CostPerAreaInput";

import { directCostUpdate } from "store/parametricAnalysis/actions";
import { useStateSelector } from "utils/field";
import { getBaseUnits } from "utils/fields";
import { getSelectedComponents } from "utils/parametric";
import { getCommSelectedComponents } from "utils/communities";
import { getCompAreaForCostCalc } from "utils/enclosure/components";
import { calcUpgradeDetailedCost, calcCommunityUpgradeCosts } from "utils/upgrades";
import {
    costTypesByUpgrade,
    overallCostTypes,
    getCodeToLabel,
    unitCostTypes,
    generationCostTypes,
} from "utils/costTypeIndex";

import classes from "./style.module.scss";
import Delete from "assets/images/icons/JSX/Delete";

const calcCostPerUnitBasis = (value, perUnitCost) => {
    return parseFloat((perUnitCost * value).toFixed(2)) || 0;
};

const CostTable = ({
    change,
    accessor,
    upgradeType,
    showCost,
    selectedUpgrade,
    selectedComponents = [],
    selectedPackage,
    isUpgradeLibrary,
    isParametricAnalysis,
    isCommunity,
    upgradeH2kType,
    directCostUpdate,
}) => {
    const { parametricAnalysis } = useStateSelector("parametricAnalysis");
    const { community } = useStateSelector("community");
    const commUpgrades = useStateSelector("commUpgrades");
    const baseModelId = commUpgrades?.baseModelId;

    const componentsFromParametric = parametricAnalysis?.model?.modelData?.components || {};
    const componentsFromCommunity =
        Object.fromEntries(
            Object.entries(community?.modelData?.models || {}).map(([modelId, { components }]) => [modelId, components])
        ) || {};
    const communityModelIds = Object.keys(componentsFromCommunity) || [];

    const costItems = useStateSelector(`${accessor}.items`) || {};
    const costTotal = useStateSelector(`${accessor}.total`) || 0;
    const commCostItems = useStateSelector(`${accessor}.communityItems`) || {};
    const communityCostTotals = useStateSelector(`${accessor}.communityTotals`) || {};

    const upgradeFields = useStateSelector("newForm.upgradeDrawer.fields") || {};

    const modelUnits = isParametricAnalysis
        ? parametricAnalysis?.model?.modelData?.uiSettings?.primaryUnits
        : useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    const costRef = useRef(null);
    const inputRef = useRef(null);

    const [isEditingCost, setIsEditingCost] = useState(false);
    const [newCost, setNewCost] = useState(0);

    let components = useStateSelector("model.modelData.components");

    components = isParametricAnalysis ? componentsFromParametric : isCommunity ? componentsFromCommunity : components;
    selectedComponents = isParametricAnalysis
        ? getSelectedComponents(upgradeType, upgradeH2kType, componentsFromParametric)
        : isCommunity
        ? getCommSelectedComponents(upgradeType, upgradeH2kType, componentsFromCommunity)
        : selectedComponents;

    // Store model's total pvCapacity 'generation' upgrade type
    const totalPvCapacity = upgradeType === "generation" ? upgradeFields?.pvCapacity || 0 : 0;

    // This variable tracks the total component area (unit value) that our cost per unit is based on.
    // For doors, it tracks the count of doors present
    // returns float
    const calcTotalComponentArea = useCallback(
        (calcType = "matCostArea") => {
            if (!isEmpty(selectedComponents) && !isUpgradeLibrary) {
                return getCompAreaForCostCalc(selectedComponents, components, upgradeType, calcType);
            }
            return 0;
        },
        [components, selectedComponents, upgradeType, isUpgradeLibrary]
    );

    // returns object of floats
    const calcMultipleTotalComponentAreas = useCallback(
        (calcType = "matCostArea") => {
            if (!isEmpty(selectedComponents)) {
                const allModelAreas = Object.entries(components).map(([modelId, modelComponents]) => {
                    const modelArea = getCompAreaForCostCalc(
                        selectedComponents[modelId],
                        modelComponents,
                        upgradeType,
                        calcType
                    );
                    return [modelId, parseFloat(modelArea.toFixed(2))];
                });
                return Object.fromEntries(allModelAreas);
            }
            return {};
        },
        [components, selectedComponents, upgradeType, isUpgradeLibrary]
    );

    // Stores one model's total area or multiple models' total areas as an object
    const totalArea = !isCommunity
        ? parseFloat(calcTotalComponentArea("matCostArea").toFixed(2))
        : calcMultipleTotalComponentAreas("matCostArea");

    //Get the total count for windows, skylights, and doors
    let totalCount = 0;
    if (upgradeType === "window" || upgradeType === "skylights") {
        totalCount = !isCommunity
            ? parseFloat(calcTotalComponentArea("matCostWindow").toFixed(2))
            : calcMultipleTotalComponentAreas("matCostWindow");
    } else if (upgradeType === "door") {
        totalCount = !isCommunity
            ? parseFloat(calcTotalComponentArea("matCostDoor").toFixed(2))
            : calcMultipleTotalComponentAreas("matCostDoor");
    }

    // Options for dropdown selection to add new cost row
    const costTypeOptions =
        costTypesByUpgrade[upgradeType]
            ?.map((code) => {
                return {
                    value: code,
                    label: getCodeToLabel(modelUnits)[code],
                };
            })
            .sort(
                (a, b) =>
                    Object.keys(getCodeToLabel(modelUnits)).indexOf(a.value) -
                    Object.keys(getCodeToLabel(modelUnits)).indexOf(b.value)
            ) || [];

    const addRow = (value) => {
        const total = 0;
        const itemId = `item-${moment().format("YYYYMMDDHHmmssSS")}`;

        change(`${accessor}.items`, {
            ...costItems,
            [itemId]: {
                label: value,
                total: total,
            },
        });

        if (isCommunity) {
            const commTotals = Object.fromEntries(communityModelIds.map((modelId) => [modelId, total]));
            const calculatedArea = ["matCostArea"].includes(value) ? { totalArea } : {};
            const calculatedCount = ["matCostWindow", "matCostDoor"].includes(value) ? { totalArea: totalCount } : {};
            const calcualtedPvCapacity = generationCostTypes.includes(value) ? { totalPvCapacity } : {};

            change(`${accessor}.communityItems`, {
                ...commCostItems,
                [itemId]: {
                    label: value,
                    total: commTotals,
                    ...calculatedArea,
                    ...calculatedCount,
                    ...calcualtedPvCapacity,
                },
            });
        }
    };

    const removeRow = (item) => {
        const { [item]: itemToRemove, ...restItems } = costItems || {};
        change(`${accessor}.items`, {
            ...restItems,
        });

        if (isCommunity) {
            const { [item]: itemToRemove, ...restCommItems } = commCostItems || {};
            change(`${accessor}.communityItems`, { 
                ...restCommItems,
            });
        }
    };

    // Update the cost items to match the base model in a community
    useEffect(() => {
        if (!isCommunity) return;

        const newItems = Object.entries(commCostItems).reduce((acc, [itemId, item]) => {
            return {
                ...acc,
                [itemId]: {
                    ...item,
                    total: item.total[baseModelId],
                },
            };
        }, {});
        change(`${accessor}.items`, newItems);
        // change(`${accessor}.total`, communityCostTotals[baseModelId]); // Duplicate change
    }, [baseModelId]);

    const [totalCost, setTotalCost] = useState(0);
    const [preTaxTotal, setPreTaxTotal] = useState(0);

    // update the total when cost items change
    useEffect(() => {
        const { totalCost = 0, preTaxTotal = 0, taxFraction = 0, postTaxRebate = 0 } = calcUpgradeDetailedCost(costItems);

        setTotalCost(parseFloat(totalCost.toFixed(2)));
        setPreTaxTotal(preTaxTotal);
    }, [costItems])
    
    useEffect(() => {
        if (upgradeType && !isEqual(totalCost, costTotal)) {
            change(`${accessor}.total`, totalCost);
        }
    }, [totalCost]);

    const inputUnit = "m2"; // always
    const displayUnit = ["Imperial", "US"].includes(modelUnits) ? "ft2" : "m2";

    const sortedItems = Object.keys(costItems).sort(
        (a, b) => parseInt(a.replace(/[^0-9\.]/g, ""), 10) - parseInt(b.replace(/[^0-9\.]/g, ""), 10)
    );

    const handleCostChange = (e) => {
        e.preventDefault();

        setIsEditingCost(false);

        const oldCost = totalCost || isCommunity ? {} : 0;

        if (oldCost == newCost) return;

        const newCostData = {
            total: parseFloat(newCost),
            items: {
                [`item-${moment().format("YYYYMMDDHHmmssSS")}`]: {
                    label: "overallCost",
                    total: parseFloat(newCost),
                },
            },
        };

        change(accessor, newCostData);
    };

    const handleNewCost = (e) => {
        // e.preventDefault();

        if (e.key === "Enter") {
            const oldCost = totalCost || isCommunity ? {} : 0;

            if (oldCost == newCost) {
                setIsEditingCost(false);

                return;
            }

            const newCostData = {
                total: parseFloat(newCost),
                items: {
                    [`item-${moment().format("YYYYMMDDHHmmssSS")}`]: {
                        label: "overallCost",
                        total: parseFloat(newCost),
                    },
                },
            };

            change(accessor, newCostData);

            setIsEditingCost(false);
        }
    };

    useEffect(() => {
        if (costRef.current && isEditingCost) {
            costRef.current.addEventListener("keydown", handleNewCost);
        }

        if (isEditingCost && inputRef.current) {
            inputRef.current.focus();
        }

        return () => {
            if (costRef.current) {
                costRef.current.removeEventListener("keydown", handleNewCost);
            }
        };
    }, [isEditingCost]);

    // Calculate and store the cost item values for each model in a community
    useEffect(() => {
        if (!upgradeType || !isCommunity || isEmpty(costItems)) return;

        const newCommCostItems = sortedItems.reduce((acc, itemId) => {
            var allModelTotals = {};

            const item = costItems[itemId];
            const pricePerUnit = item?.pricePerUnit || 0;
            const tax = item?.tax || 0;
            const label = item?.label || "";

            if (overallCostTypes.includes(label)) {
                allModelTotals = Object.fromEntries(
                    communityModelIds.map((modelId) => [
                        modelId,
                        item.total || 0,
                    ])
                );
            } else if (label === "tax") {
                // postTaxTotalObj
                allModelTotals = Object.fromEntries(
                    communityModelIds.map((modelId) => [
                        modelId,
                        parseFloat(((tax / 100) * preTaxTotal).toFixed(2)) || 0,
                    ])
                );
            } else if (upgradeType === "generation") {
                // totalPvPriceObj
                allModelTotals = Object.fromEntries(
                    communityModelIds.map((modelId) => [
                        modelId,
                        calcCostPerUnitBasis(totalPvCapacity, pricePerUnit) || 0,
                    ])
                );
            } else {
                // totalPriceObj
                // Need to know if we're using the component area (DEFAULT) or count (for windows, skylights, and doors)
                const unitValueForCalculation = ["matCostWindow", "matCostDoor"].includes(label) ? totalCount : totalArea;

                allModelTotals = Object.fromEntries(
                    communityModelIds.map((modelId) => [
                        modelId,
                        calcCostPerUnitBasis(unitValueForCalculation[modelId], pricePerUnit) || 0,
                    ])
                );
            }

            return {
                ...acc,
                [itemId]: {
                    ...(commCostItems[itemId] || {}),
                    ...item,
                    total: allModelTotals,
                },
            };
        }, {});

        change(`${accessor}.communityItems`, newCommCostItems);
        change(`${accessor}.communityTotals`, calcCommunityUpgradeCosts(newCommCostItems));
    }, [costItems]);

    const ItemsList = sortedItems.map((item, ind) => {
        // per unit cost will change depending on modelUnits
        const pricePerUnit = costItems[item]?.pricePerUnit || 0;
        const tax = costItems[item]?.tax || 0;
        const unit = displayUnit;
        const label = costItems[item]?.label || "";

        //Need to know if we're using the component area (DEFAULT) or count (for windows, skylights, and doors)
        const unitValueForCalculation = ["matCostWindow", "matCostDoor"].includes(label) ? totalCount : totalArea;

        return (
            <div key={ind} className={`${classes.tableRow} ${ind % 2 !== 1 && classes.oddRow}`}>
                <Field
                    component={Select}
                    className={`${classes.costSelect} ${ind % 2 !== 1 && classes.oddRow}`}
                    name={`${accessor}.items.${item}.label`}
                    options={costTypeOptions}
                />
                <div className={classes.endRow}>
                    {overallCostTypes.includes(costItems[item].label) ? (
                        <Field
                            className={classes.amount}
                            component={Input}
                            name={`${accessor}.items.${item}.total`}
                            type="number"
                        />
                    ) : costItems[item].label === "tax" ? (
                        <div className={classes.calcFields}>
                            <Field
                                // label="$/Unit"
                                placeholder="% tax"
                                className={classes.amount}
                                component={Input}
                                type="number"
                                name={`${accessor}.items.${item}.tax`}
                            />
                            <Field
                                className={classes.amount}
                                component={Input}
                                name={`${accessor}.items.${item}.total`}
                                setValue={parseFloat(((tax / 100) * preTaxTotal).toFixed(2)) || 0}
                                type="number"
                                disabled
                            />
                        </div>
                    ) : (
                        <div style={{ display: "flex" }}>
                            {["window", "skylights", "door"].includes(upgradeType) ? (
                                <div className={classes.calcFields}>
                                    <Field
                                        placeholder="$/unit"
                                        className={classes.amount}
                                        component={Input}
                                        name={
                                            !isCommunity
                                                ? `${accessor}.items.${item}.totalArea`
                                                : `${accessor}.communityItems.${item}.totalArea.${baseModelId}`
                                        }
                                        disabled
                                        setValue={
                                            !isCommunity
                                                ? unitValueForCalculation
                                                : unitValueForCalculation[baseModelId] || 0
                                        }
                                        type="number"
                                    />
                                    <Field
                                        placeholder="$/unit"
                                        className={classes.amount}
                                        component={Input}
                                        name={`${accessor}.items.${item}.pricePerUnit`}
                                        type="number"
                                    />
                                    <Field
                                        className={classes.amount}
                                        component={Input}
                                        type="number"
                                        name={`${accessor}.items.${item}.total`}
                                        setValue={!isCommunity
                                            ? calcCostPerUnitBasis(unitValueForCalculation, pricePerUnit) || 0
                                            : calcCostPerUnitBasis(unitValueForCalculation[baseModelId], pricePerUnit) || 0}
                                        disabled
                                    />
                                </div>
                            ) : upgradeType === "generation" ? (
                                <div className={classes.calcFields}>
                                    <Field
                                        className={classes.amount}
                                        component={InputWithUnits}
                                        name={`${accessor}.items.${item}.totalPvCapacity`}
                                        decimals={2}
                                        setValue={totalPvCapacity}
                                        disabled
                                        setTouched={true}
                                        change={change}
                                        units={{
                                            base: { trueBase: "kW", displayBase: "kW" },
                                            options: ["kW"],
                                            selected: "kW",
                                            unitType: "power",
                                            accessor: `${accessor}.items.${item}.totalPvCapacity_u`,
                                        }}
                                    />
                                    <Field
                                        className={classes.amount}
                                        component={CostPerAreaInput}
                                        name={`${accessor}.items.${item}.pricePerUnit`}
                                        decimals={2}
                                        placeholder="$/kW"
                                        setTouched={true}
                                        change={change}
                                        units={{
                                            base: { trueBase: "kW", displayBase: "kW" }, //always working in base units
                                            selected: "kW",
                                            unitType: "power",
                                            accessor: `${accessor}.items.${item}.pricePerUnit_u`,
                                        }}
                                    />
                                    <Field
                                        className={classes.amount}
                                        component={Input}
                                        type="number"
                                        name={`${accessor}.items.${item}.total`}
                                        setValue={calcCostPerUnitBasis(totalPvCapacity, pricePerUnit) || 0} //always do calculation on base value
                                        disabled
                                    />
                                </div>
                            ) : (
                                <div className={classes.calcFields}>
                                    <Field
                                        className={classes.amount}
                                        component={InputWithUnits}
                                        name={
                                            !isCommunity
                                                ? `${accessor}.items.${item}.totalArea`
                                                : `${accessor}.communityItems.${item}.totalArea.${baseModelId}`
                                        }
                                        decimals={2}
                                        // setValue={totalArea}
                                        setValue={!isCommunity ? totalArea : totalArea[baseModelId]}
                                        disabled
                                        setTouched={true}
                                        change={change}
                                        units={{
                                            base: getBaseUnits("commonAreaFloors", modelUnits),
                                            // options: ["m2", "ft2"],
                                            selected: unit,
                                            unitType: "area",
                                            accessor: `${accessor}.items.${item}.totalArea_u`,
                                        }}
                                    />
                                    <Field
                                        className={classes.amount}
                                        component={CostPerAreaInput}
                                        name={`${accessor}.items.${item}.pricePerUnit`}
                                        decimals={2}
                                        placeholder="$/unit"
                                        setTouched={true}
                                        change={change}
                                        units={{
                                            base: { trueBase: "m2", displayBase: displayUnit },
                                            selected: displayUnit,
                                            unitType: "pricePerArea",
                                            accessor: `${accessor}.items.${item}.pricePerUnit_u`,
                                        }}
                                    />
                                    <Field
                                        className={classes.amount}
                                        component={Input}
                                        type="number"
                                        name={`${accessor}.items.${item}.total`}
                                        setValue={
                                            !isCommunity
                                                ? calcCostPerUnitBasis(totalArea, pricePerUnit) || 0
                                                : calcCostPerUnitBasis(totalArea[baseModelId], pricePerUnit) || 0
                                        } //always do calculation on base value
                                        disabled
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <IconButton onClick={() => removeRow(item)} style={{ marginLeft: "0rem", marginRight: "0rem" }}>
                        <Delete />
                    </IconButton>
                </div>
            </div>
        );
    });

    return (
        <div style={{ marginTop: "1rem" }}>
            <Accordion
                heading={
                    <div className={classes.accordionHeader} ref={costRef}>
                        <p>Upgrade Costs</p>
                        <div className={classes.costPreview}>
                            <CostTableHeader
                            //  isOpen={open}
                            >
                                {
                                    isEditingCost ? (
                                        <span style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                            $
                                            <Input
                                                type={"number"}
                                                hideLabel
                                                input={{
                                                    value: newCost,
                                                    onChange: (e) => setNewCost(e),
                                                    ref: inputRef,
                                                }}
                                                onBlurAction={handleCostChange}
                                                style={{ fontSize: "16px", width: "7rem" }}
                                            />
                                        </span>
                                    ) : isParametricAnalysis ? (
                                        <span
                                            className={classes.costSpan}
                                            onClick={() => {
                                                setIsEditingCost(true);

                                                setNewCost(totalCost);
                                            }}
                                        >
                                            ${totalCost}
                                        </span>
                                    ) : (
                                        <Field
                                            className={classes.amount}
                                            component={CostTableInput}
                                            name={`${accessor}.total`}
                                            setValue={totalCost}
                                            // name={
                                            //     !isCommunity ? `${accessor}.total` : `${accessor}.total.${baseModelId}`
                                            // }
                                            // setValue={!isCommunity ? totalCost : totalCost[baseModelId]}
                                            disabled
                                        />
                                    )

                                    //     component={CostTableInput}
                                    //     name={`${accessor}.total`}
                                    //     setValue={totalCost}
                                    //     onClick={() => {
                                    //         setIsEditingCost(true);

                                    //         // setNewCost(
                                    //         //     upgrade.packageValues?.cost
                                    //         //         ? upgrade.packageValues?.cost[upgrade.packageValues.upgradeSubType]?.total || 0
                                    //         //         : 0
                                    //         // );
                                    //     }}
                                    // />
                                }
                            </CostTableHeader>
                        </div>
                    </div>
                }
                large
            >
                <div className={classes.table}>
                    <div className={classes.tableHeader}>
                        <p>Line Item</p>
                        <p>Amount</p>
                    </div>
                </div>
                <div className={classes.tableBody}>
                    {ItemsList}
                    <div className={classes.tableRow}>
                        <Select
                            className={classes.costSelect}
                            input={{
                                onChange: (value) => {
                                    addRow(value);
                                },
                                value: null,
                            }}
                            options={costTypeOptions}
                            placeholder="Select new line item"
                        />
                    </div>

                    <InputRow gridTemplate="2fr 1fr" className={classes.total}>
                        <p>Total</p>
                        <Field
                            className={classes.amount}
                            component={CostTableInput}
                            name={`${accessor}.total`}
                            setValue={totalCost}
                            // name={!isCommunity ? `${accessor}.total` : `${accessor}.total.${baseModelId}`}
                            // setValue={!isCommunity ? totalCost : totalCost[baseModelId]}
                            disabled
                        />
                    </InputRow>
                    {isUpgradeLibrary && (
                        <div className={classes.upgradeLibraryText}>
                            <p>Costs per unit will be updated when applying this upgrade to a model.</p>
                        </div>
                    )}
                </div>
            </Accordion>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({});

const mergeProps = (state, dispatch, own) => ({
    ...state,
    ...dispatch,
    ...own,
});

const mapDispatchToProps = (dispatch) => ({
    directCostUpdate: (sectionType, upgradeId, newCostData) =>
        dispatch(directCostUpdate(sectionType, upgradeId, newCostData)),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(CostTable);
